import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import {
  Search, Globe, Calendar, User, Package, 
  Loader2, ArrowRight, X, RefreshCw, Tag
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("es-ES", {
      day: "2-digit", month: "short", year: "numeric"
    });
  } catch {
    return dateStr;
  }
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  return `hace ${Math.floor(hrs / 24)}d`;
}

export default function OnlineBookings() {
  const navigate = useNavigate();
  const { darkMode } = useSettings();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const params = { status: "online_pending" };
      if (q.trim()) params.search = q.trim();
      const { data } = await axios.get(`${API}/api/online-bookings`, { headers, params });
      setBookings(data);
    } catch {
      toast.error("Error al cargar las reservas online");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const handleDeliver = (booking) => {
    navigate(`/nuevo-alquiler?booking_id=${booking.id}`);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await axios.post(`${API}/api/online-bookings/${cancelTarget.id}/cancel`, {}, { headers });
      toast.success(`Reserva ${cancelTarget.localizador} cancelada`);
      setCancelTarget(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cancelar");
    }
  };

  const bg = darkMode ? "bg-slate-900" : "bg-slate-50";
  const cardBg = darkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const textPrimary = darkMode ? "text-white" : "text-slate-900";
  const textSecondary = darkMode ? "text-slate-600" : "text-slate-500";

  return (
    <div className={`p-6 lg:p-8 min-h-screen ${bg}`} data-testid="online-bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Globe className="h-5 w-5 text-white" />
            </div>
            <h1 className={`text-3xl font-black ${textPrimary}`}>Reservas Online</h1>
          </div>
          <p className={`text-sm ${textSecondary} ml-[52px]`}>
            Sala de espera de reservas recibidas desde webs externas
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => load(search)}
          className="gap-2 self-start sm:self-auto"
          data-testid="refresh-bookings-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-600 pointer-events-none" />
        <Input
          data-testid="booking-search-input"
          className={`pl-12 h-12 text-base rounded-xl border-2 focus:ring-2 focus:ring-blue-500/20 ${
            darkMode
              ? "bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
              : "bg-white border-slate-200 placeholder:text-slate-600"
          }`}
          placeholder="Buscar por nombre del cliente o localizador (ej. #RES-1234)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : bookings.length === 0 ? (
        <div className={`rounded-2xl border-2 border-dashed p-16 text-center ${
          darkMode ? "border-slate-700" : "border-slate-200"
        }`}>
          <Globe className={`h-14 w-14 mx-auto mb-4 ${textSecondary} opacity-30`} />
          <p className={`text-xl font-bold ${textPrimary} mb-2`}>
            {search ? "Sin resultados" : "Sin reservas pendientes"}
          </p>
          <p className={`text-sm ${textSecondary}`}>
            {search
              ? "Prueba con otro nombre o localizador"
              : "Las reservas de tu web aparecerán aquí en cuanto lleguen"}
          </p>
        </div>
      ) : (
        <>
          <p className={`text-sm ${textSecondary} mb-4`}>
            {bookings.length} reserva{bookings.length !== 1 ? "s" : ""} pendiente{bookings.length !== 1 ? "s" : ""}
          </p>
          <div className="space-y-3">
            {bookings.map((b) => (
              <Card
                key={b.id}
                className={`${cardBg} border hover:shadow-md transition-all duration-200`}
                data-testid={`booking-row-${b.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Left: booking info */}
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Top row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className="font-mono text-sm font-bold border-blue-400 text-blue-600 bg-blue-50"
                          data-testid="booking-localizador"
                        >
                          {b.localizador}
                        </Badge>
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Globe className="h-3 w-3" />
                          Web
                        </Badge>
                        {b.customer_id ? (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-emerald-300 text-emerald-700 bg-emerald-50"
                            data-testid={`booking-customer-linked-${b.id}`}
                          >
                            <User className="h-3 w-3" />
                            Ficha vinculada
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-amber-300 text-amber-700 bg-amber-50"
                            data-testid={`booking-customer-pending-${b.id}`}
                          >
                            <User className="h-3 w-3" />
                            Sin ficha
                          </Badge>
                        )}
                        {(Number(b.amount) > 0 || Number(b.deposit) > 0) && (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-blue-300 text-blue-700 bg-blue-50"
                            data-testid={`booking-prepaid-${b.id}`}
                          >
                            Pagado online: {formatCurrency(Number(b.amount || 0))}
                            {Number(b.deposit) > 0 && ` · Dep ${formatCurrency(Number(b.deposit))}`}
                          </Badge>
                        )}
                        <span className={`text-xs ${textSecondary}`}>{timeAgo(b.created_at)}</span>
                      </div>

                      {/* Client */}
                      <div className="flex items-center gap-2">
                        <User className={`h-4 w-4 flex-shrink-0 ${textSecondary}`} />
                        <span className={`font-semibold ${textPrimary} truncate`}>{b.client_name}</span>
                        {b.client_phone && (
                          <span className={`text-sm ${textSecondary}`}>· {b.client_phone}</span>
                        )}
                      </div>

                      {/* Dates */}
                      <div className="flex items-center gap-2">
                        <Calendar className={`h-4 w-4 flex-shrink-0 ${textSecondary}`} />
                        <span className={`text-sm ${textSecondary}`}>
                          {formatDate(b.start_date)}
                          <ArrowRight className="inline h-3 w-3 mx-1" />
                          {formatDate(b.end_date)}
                        </span>
                      </div>

                      {/* Categories */}
                      <div className="flex items-start gap-2">
                        <Tag className={`h-4 w-4 flex-shrink-0 mt-0.5 ${textSecondary}`} />
                        <div className="flex flex-wrap gap-1">
                          {b.categories.map((cat, i) => (
                            <span
                              key={i}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                darkMode
                                  ? "bg-slate-700 text-slate-300"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>

                      {b.notes && (
                        <p className={`text-xs italic ${textSecondary} truncate`}>
                          "{b.notes}"
                        </p>
                      )}
                    </div>

                    {/* Right: actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCancelTarget(b)}
                        className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                        data-testid={`cancel-booking-btn-${b.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDeliver(b)}
                        className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md font-semibold px-5"
                        data-testid={`deliver-btn-${b.id}`}
                      >
                        <Package className="h-4 w-4" />
                        Entregar Material
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Cancel dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar reserva?</AlertDialogTitle>
            <AlertDialogDescription>
              Se cancelará la reserva <strong>{cancelTarget?.localizador}</strong> de{" "}
              <strong>{cancelTarget?.client_name}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
              Cancelar reserva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
